import React, { useState, useEffect } from 'react'
import { useWallet } from '../contexts/WalletContext'
import { 
  Code, 
  Zap, 
  Shield, 
  Globe, 
  Rocket, 
  Database,
  Link,
  FileText,
  Play,
  Download,
  Star,
  CheckCircle,
  ArrowRight,
  Terminal,
  Cpu,
  Lock
} from 'lucide-react'

interface ContractInfo {
  id: string
  name: string
  description: string
  status: 'deployed' | 'testing' | 'development'
  address?: string
  functions: string[]
  lastDeployed?: string
}

export default function BlockchainDevelopment() {
  const { address, isConnected, client } = useWallet()
  const [contracts, setContracts] = useState<ContractInfo[]>([
    {
      id: '1',
      name: 'TemplateNFT',
      description: 'Smart contract for minting and managing template NFTs',
      status: 'deployed',
      address: '0x1234567890abcdef1234567890abcdef12345678',
      functions: ['mint_template', 'create_collection', 'use_template', 'transfer_template'],
      lastDeployed: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      name: 'SplitRule',
      description: 'Revenue sharing mechanism for content creators',
      status: 'deployed',
      address: '0xabcdef1234567890abcdef1234567890abcdef12',
      functions: ['create_rule', 'distribute', 'create_recipient'],
      lastDeployed: '2024-01-14T15:45:00Z'
    },
    {
      id: '3',
      name: 'TransferPolicy',
      description: 'Policy enforcement for paid transfers',
      status: 'testing',
      functions: ['new', 'execute_sale']
    }
  ])
  const [selectedContract, setSelectedContract] = useState<ContractInfo | null>(null)
  const [isDeploying, setIsDeploying] = useState(false)

  const handleDeploy = async (contractId: string) => {
    setIsDeploying(true)
    // Simulate deployment process
    await new Promise(resolve => setTimeout(resolve, 3000))
    
    setContracts(prev => prev.map(contract => 
      contract.id === contractId 
        ? { 
            ...contract, 
            status: 'deployed' as const,
            address: '0x' + Math.random().toString(16).substr(2, 40),
            lastDeployed: new Date().toISOString()
          }
        : contract
    ))
    setIsDeploying(false)
  }

  const handleTest = async (contractId: string) => {
    // Simulate testing process
    await new Promise(resolve => setTimeout(resolve, 2000))
    console.log('Running tests for contract:', contractId)
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <div className="text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-gold-500 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Terminal className="w-12 h-12 text-black" />
            </div>
            <h1 
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Blockchain Development Hub
            </h1>
            <p className="text-purple-200 mb-6 max-w-md mx-auto">
              Connect your wallet to access smart contract development tools and deployment features
            </p>
            <button className="px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-black font-semibold rounded-lg hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
              Connect Wallet
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{
           background: `
             linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
             radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
           `,
           backgroundSize: '100% 100%, 800px 800px, 900px 900px'
         }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-40 right-20 w-1 h-1 bg-purple-400 rounded-full animate-pulse opacity-40" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-3 h-3 bg-violet-400 rounded-full animate-pulse opacity-30" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '0.5s' }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-4xl font-bold mb-4 bg-gradient-to-r from-gold-400 via-gold-500 to-violet-400 bg-clip-text text-transparent"
            style={{
              fontFamily: 'Georgia, serif',
              textShadow: `
                1px 1px 0px rgba(0, 0, 0, 0.4),
                -1px -1px 0px rgba(0, 0, 0, 0.4),
                1px -1px 0px rgba(0, 0, 0, 0.4),
                -1px 1px 0px rgba(0, 0, 0, 0.4),
                0px 0px 4px rgba(0, 0, 0, 0.3)
              `
            }}
          >
            🚀 Blockchain Development Hub
          </h1>
          <p className="text-purple-200 text-lg max-w-2xl mx-auto">
            Deploy, test, and manage your Sui smart contracts with our integrated development environment
          </p>
        </div>

        {/* Development Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <Code className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-sm text-blue-400 font-medium">Contracts</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {contracts.length}
            </div>
            <div className="text-sm text-blue-300">
              Total contracts
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-sm text-green-400 font-medium">Deployed</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {contracts.filter(c => c.status === 'deployed').length}
            </div>
            <div className="text-sm text-green-300">
              Live contracts
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <Zap className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-sm text-purple-400 font-medium">Functions</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {contracts.reduce((acc, contract) => acc + contract.functions.length, 0)}
            </div>
            <div className="text-sm text-purple-300">
              Total functions
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-6 h-6 text-orange-400" />
              </div>
              <span className="text-sm text-orange-400 font-medium">Security</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              100%
            </div>
            <div className="text-sm text-orange-300">
              Audit score
            </div>
          </div>
        </div>

        {/* Contract Management */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contract List */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl p-6">
            <h2 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              📋 Smart Contracts
            </h2>
            <div className="space-y-4">
              {contracts.map((contract) => (
                <div 
                  key={contract.id}
                  className={`bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 cursor-pointer ${
                    selectedContract?.id === contract.id ? 'ring-2 ring-gold-400' : ''
                  }`}
                  onClick={() => setSelectedContract(contract)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                        <Code className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{contract.name}</h3>
                        <p className="text-purple-300 text-sm">{contract.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        contract.status === 'deployed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : contract.status === 'testing'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {contract.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-purple-400">
                      {contract.functions.length} functions
                    </div>
                    <div className="flex gap-2">
                      {contract.status === 'development' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeploy(contract.id)
                          }}
                          disabled={isDeploying}
                          className="px-3 py-1 bg-gradient-to-r from-gold-500 to-gold-600 text-black text-xs font-semibold rounded hover:from-gold-600 hover:to-gold-700 transition-all duration-300 disabled:opacity-50"
                        >
                          {isDeploying ? 'Deploying...' : 'Deploy'}
                        </button>
                      )}
                      {contract.status === 'testing' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleTest(contract.id)
                          }}
                          className="px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-semibold rounded hover:from-purple-600 hover:to-purple-700 transition-all duration-300"
                        >
                          Test
                        </button>
                      )}
                      {contract.status === 'deployed' && (
                        <div className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3" />
                          Deployed
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contract Details */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl p-6">
            <h2 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              🔍 Contract Details
            </h2>
            {selectedContract ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{selectedContract.name}</h3>
                  <p className="text-purple-200">{selectedContract.description}</p>
                </div>

                {selectedContract.address && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Contract Address</h4>
                    <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-3">
                      <code className="text-green-400 text-sm break-all">{selectedContract.address}</code>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-white font-semibold mb-3">Functions</h4>
                  <div className="space-y-2">
                    {selectedContract.functions.map((func, index) => (
                      <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-3">
                        <code className="text-blue-400">{func}</code>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedContract.lastDeployed && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Last Deployed</h4>
                    <p className="text-purple-300 text-sm">
                      {new Date(selectedContract.lastDeployed).toLocaleString()}
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center justify-center gap-2">
                    <FileText className="w-4 h-4" />
                    View Source
                  </button>
                  <button className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300 flex items-center justify-center gap-2">
                    <Play className="w-4 h-4" />
                    Interact
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Code className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                <p className="text-purple-300">Select a contract to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Development Tools */}
        <div className="mt-8 bg-gradient-to-r from-gold-500/20 to-purple-500/20 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
          <h3 
            className="text-2xl font-bold text-white text-center mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            🛠️ Development Tools
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 backdrop-blur-sm border border-blue-400/30 rounded-lg p-6 transition-all duration-300 group text-center">
              <Terminal className="w-8 h-8 text-blue-400 mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold mb-2">Move Compiler</div>
              <div className="text-blue-300 text-sm">Compile and validate Move contracts</div>
            </button>
            
            <button className="bg-gradient-to-r from-green-500/20 to-green-600/20 hover:from-green-500/30 hover:to-green-600/30 backdrop-blur-sm border border-green-400/30 rounded-lg p-6 transition-all duration-300 group text-center">
              <Zap className="w-8 h-8 text-green-400 mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold mb-2">Test Runner</div>
              <div className="text-green-300 text-sm">Run unit tests and validations</div>
            </button>
            
            <button className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 hover:from-purple-500/30 hover:to-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-lg p-6 transition-all duration-300 group text-center">
              <Rocket className="w-8 h-8 text-purple-400 mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold mb-2">Deploy Manager</div>
              <div className="text-purple-300 text-sm">Deploy to testnet and mainnet</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
